import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-childrens-home-records';

export const RequestChildrensHomeRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        If you lived in a children&apos;s home — sometimes called residential care — rather than
        with foster carers, the organisation is a much simpler target for a Subject Access Request
        than a foster placement is: there is no individual carer to untangle from an
        organisation, just the home itself. But it is worth knowing who actually ran it, because
        most children&apos;s homes in England are not run by a council at all.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Who to ask" title="The registered provider, not necessarily the council">
      <p>
        Every children&apos;s home in England has to be registered with Ofsted, and the legal
        responsibility for it sits with whoever registered it — the{' '}
        <strong className="text-ink">registered provider</strong>. As of the most recent official
        figures, the large majority of children&apos;s homes in England are privately owned, not
        run by a council or a charity — so the organisation that holds your case records is very
        often a private company, entirely separate from the council that placed you there.
      </p>
      <GuideChecklist
        items={[
          'The registered provider that ran the home holds your case records — request from them directly.',
          "The council that placed you there is a separate data controller, holding its own case file (the same kind of material covered in our guide to requesting care records from the council) — it's worth requesting from both.",
          "If you're not sure who ran a specific home, Ofsted's public register can usually confirm the registered provider for a given home, though it isn't itself a source of your personal records.",
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="What to ask for" title="What a children's home is legally required to record">
      <p>
        The{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2015/541/regulation/36">
          Children&apos;s Homes (England) Regulations 2015
        </SourceLink>{' '}
        set out, in detail, exactly what a home has to keep on file for every child. Ask for all
        of it, not just "my file":
      </p>
      <GuideChecklist
        items={[
          'Your full case record, kept up to date and signed and dated by whoever wrote each entry.',
          'Placing authority, parent, social worker, school and GP/dentist contact details held about you at the time.',
          <>
            <strong className="text-ink">
              Every missing-from-care incident recorded against you
            </strong>{' '}
            — the date and circumstances of each one. This is a named, specific requirement of
            the regulations, not something homes can leave out as too minor to record.
          </>,
          'Care and placement plans, and records of reviews.',
          'EHC plans or SEN statements, and school reports held at the home.',
          'Health, immunisation, allergy and medication records.',
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="A specific, powerful right" title="Restraint, control and discipline records">
      <p>
        If you were ever physically restrained, or subject to a control or discipline measure,
        the regulations require a detailed record within a strict timescale — and you are entitled
        to see it. Within 24 hours of any use of restraint, a written record must be made naming
        the child, the behaviour that led to it, the date, time and location, exactly what measure
        was used and for how long, what de-escalation was tried first, who carried it out and who
        witnessed it, its effectiveness, and any injury or treatment that followed. It must then
        be signed off within 48 hours, and the home must speak to the child about it within five
        days.
      </p>
      <p>
        That is a lot of required detail, on a strict clock, for every single incident — which
        means a genuine restraint episode from your time there should have a correspondingly
        detailed record behind it. If what comes back reads differently from how you remember it,
        or reads coldly and without the context you&apos;d expect, see our guide on{' '}
        <Link to="/guides/what-to-do-if-your-records-are-wrong-or-unkind" className="text-brand underline hover:opacity-90">
          what to do if something in your records is wrong, or was written unkindly
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA />

    <GuideSection eyebrow="If the home has closed" title="Records don't disappear when a home does">
      <p>
        The same regulations require a plan for exactly this. If the provider still runs another
        children&apos;s home, your case record should have transferred there. If not, it should
        have gone to the local authority that placed you — or, for education-specific material, to
        whichever authority was responsible for your EHC or SEN provision. Ask the council that
        placed you who now holds it if you can&apos;t trace the provider directly.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Retention" title="How long they have to keep it">
      <p>
        Case records must be kept for{' '}
        <strong className="text-ink">75 years from your date of birth</strong> — one of the
        longest retention periods in this whole area of law, so a home telling you records from
        decades ago no longer exist is worth questioning closely. Where a child died before
        turning 18, the period is instead 15 years from the date of death.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Ofsted's role" title="A regulator, not usually a records source — with one exception">
      <p>
        Ofsted inspects and registers children&apos;s homes; it is not where your day-to-day case
        history lives. But homes are required to notify Ofsted of certain serious events — sexual
        exploitation, serious police-involved incidents, abuse allegations, child protection
        enquiries, and other serious incidents — and Ofsted keeps a record of those notifications
        for at least as long as the home stayed registered, longer if concerns were raised. If you
        believe something serious involving you was notified to Ofsted, it is worth sending Ofsted
        its own Subject Access Request as well.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="childrens-home-faq">
        <GuideFaqItem
          q="I moved between several children's homes. Do I need to request from each provider separately?"
          a="Yes, if they were run by different organisations — which is common, since a placement move often means a different provider entirely, not just a different building. List every home you remember and check who ran each one."
        />
        <GuideFaqItem
          q="Is a semi-independent or supported living placement covered the same way?"
          a="Broadly similar record-keeping expectations apply to registered provision for older teenagers, though the exact regulatory detail differs by placement type. It's still worth requesting from whichever organisation ran the specific setting."
        />
        <GuideFaqItem
          q="Can I find out which staff member restrained me, or wrote a specific entry?"
          a="The regulations require every case record entry to be signed and dated by its author, and every restraint record to name who carried it out and who witnessed it — so this should already be in what you receive, not something you have to ask for separately."
        />
        <GuideFaqItem
          q="What if the provider has gone out of business entirely, not just changed which home it runs?"
          a="The records still have to have gone somewhere under the same transfer duty — usually to the placing council, since that's the fallback the regulations point to when a provider stops operating. Start there."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The record-keeping, restraint-recording and transfer duties are all set out in the{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2015/541/regulation/36">
          Children&apos;s Homes (England) Regulations 2015
        </SourceLink>
        , particularly{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2015/541/regulation/36">
          regulation 36
        </SourceLink>{' '}
        and{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2015/541/schedule/3">
          Schedule 3
        </SourceLink>{' '}
        (what a case record must contain), and{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2015/541/regulation/35">
          regulation 35
        </SourceLink>{' '}
        (restraint records). Ownership figures for children&apos;s homes in England are published by
        the{' '}
        <SourceLink href="https://www.gov.uk/government/statistics/ownership-of-childrens-social-care-providers-in-england-2025/main-findings-ownership-of-childrens-social-care-providers-in-england-2025">
          Department for Education
        </SourceLink>
        . For the general mechanics of a SAR, see our{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="Add every home you remember"
      body="We'll help you work out who ran each one, draft a separate request for each provider, and track every deadline — so a placement move never means a gap in what you ask for."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestChildrensHomeRecords;
